/* eslint-disable prefer-template, no-param-reassign */
import Config from 'es6/core/config';
import Logger from 'es6/services/logger';
import QuizBase from 'es6/controllers/apps/quizzes/quiz-base';
import { ResultTypes } from 'es6/models/quiz-result';
import StudyScreen from 'es6/components/apps/screens/study';
import AnswerConfirmationScreen, {
  MULTIPLE_CHOICE_ANSWER,
  FILL_IN_THE_BLANK_ANSWER,
  STUDY_LESS,
  STUDY_MORE,
} from 'es6/components/apps/screens/answer-confirmation';

/**
 * Renders and starts the phase for the study screen.
 *
 * @this ItemQuizBase
 */
function showStudyScreen() {
  const { _screenManager } = this;
  const onRemove = _screenManager.fireEvent.bind(_screenManager, 'skip_item_initiated');

  this.result.beginPhase('study');
  _screenManager.renderComponent(StudyScreen, {
    itemPackage: this.itemPackage,
    onNext: this.finishQuiz.bind(this),
    onRemove,
    showTranscription: Config.showTranscriptions,
  });
}

/**
 * Renders the study screen if not a diagnostic quiz. Otherwise,
 * finishes the quiz.
 *
 * @this ItemQuizBase
 */
function showStudyIfAppropriate() {
  if (!this._isDiagnosticQuiz) {
    showStudyScreen.call(this);
  } else {
    this.finishQuiz();
  }
}

export class ItemQuizBase extends QuizBase {
  constructor(itemPackage, ...args) {
    super('items', itemPackage.getId(), ...args);

    this.itemPackage = itemPackage;
    this.paused = false;
    this.timeLimit = 8;

    this._initSentence();
  }

  show() {
    Logger.info('Quiz', 'Showing ' + this.quizType + ' quiz for ' + this.itemPackage.item.cue.text, this);

    this._prepopulateScreens();
    this._showAndTimePhase('recall');
  }

  recallWasAnswered(known) {
    if (known) {
      if (this._quizTimer) {
        this._quizTimer.start(this.timeLimit);
      }
      this._showAndTimePhase(this.screen, 'quiz');
    } else {
      this.result.setResult(ResultTypes.YIELD_ON_RECALL);
      showStudyIfAppropriate.call(this);
    }
  }

  quizWasAnswered(resultObject, imeHtml) {
    this.result.setResult(resultObject.result);

    // In some cases, the result object contains the quizData on top of it.
    this.updateQuizData(resultObject);

    this.result.beginPhase('confirmation');

    if (this._quizTimer) {
      this._quizTimer.disable();
    }

    // Show the confirmation screen (incorrect confirmation is handled by the
    // quiz screen itself).
    if (this.result.isCorrect()) {
      this._showAnswerConfirmationScreen(imeHtml);
    } else {
      this._imeHtml = imeHtml;
    }
  }

  returnedFromQuiz(chosen) {
    switch (chosen) {
      // Mark as correct from the incorrect confirmation screen.
      case 'correct':
        // Show dialogue, which may result in quizMarkedAsCorrect being called.
        Smartfm.Apps.Modals.markAsCorrectDialogue.show();
        break;

      // Review from the correct confirmation screen.
      case 'review':
        // Redirect to study screen.
        showStudyScreen.call(this);
        break;

      // Yield from the quiz screen.
      case 'dont_know':
        this.result.setResult(ResultTypes.YIELD_ON_QUIZ);
        if (this._quizTimer) {
          this._quizTimer.disable();
        }
        showStudyIfAppropriate.call(this);
        break;

      // Yield from quiz result screen.
      case 'incorrect':
        this.result.setResult(ResultTypes.YIELD_ON_QUIZ);
        Smartfm.Apps.Sounds.incorrect.play();
        showStudyScreen.call(this);
        break;

      // Learning engine hints on correct confirmation screen.
      case 'easy':
        this.result.setResult(ResultTypes.EASY);
        this.finishQuiz();
        break;

      case 'hard':
        this.result.setResult(ResultTypes.HARD);
        this.finishQuiz();
        break;

      case 'next':
        // This quiz is finished.
        this.finishQuiz();
        break;

      default:
        throw new Error('Unknown user choice');
    }
  }

  quizMarkedAsCorrect() {
    this.result.setResult(ResultTypes.CORRECT);
    this.result.beginPhase('confirmation');
    if (this._quizTimer) {
      this._quizTimer.disable();
    }
    this._showAnswerConfirmationScreen(this._imeHtml);
  }

  _showAnswerConfirmationScreen(imeHtml) {
    const answerType = this.screen === 'fill_in_the_blank' ?
      FILL_IN_THE_BLANK_ANSWER :
      MULTIPLE_CHOICE_ANSWER;

    const answerText =
      // Quizzes that show the item's transliteration should also show the transliteration on the
      // answer confirmation screen.
      this.quizContent === 'transliteration' ? this.quizObject.getTransliteration() || this.quizObject.getText() :
      this.quizObject.getText();

    const answerLanguage = this.quizObject.language;

    const sentence = this.sentence ? {
      imageUrl: this.sentence.getImage(),
      soundUrl: this.sentence.getSound(),
      text: this.sentence.getText(),
      transliteration: this.sentence.getTransliteration(),
      translation: this.sentence.getTranslation(),
      language: this.sentence.cue.language,
    } : null;

    this._screenManager.renderComponent(
      AnswerConfirmationScreen,
      {
        answerType,
        answerText,
        answerLanguage,
        imeHtml, // Used to reconstruct styles of FIB answers.
        sentence,
        onRemove: () => this._screenManager.fireEvent('skip_item_initiated'),
        onReview: () => this.returnedFromQuiz('review'),
        onNext: () => this.returnedFromQuiz('next'),
        onIncorrect: () => this.returnedFromQuiz('incorrect'),
        onStudyLessMore: (lessOrMore) => {
          this.returnedFromQuiz({
            [STUDY_LESS]: 'easy',
            [STUDY_MORE]: 'hard',
          }[lessOrMore]);
        },
      }
    );
  }

  _prepopulateScreens() {
    this._screenManager.populate('recall', this, this._isDiagnosticQuiz);
    this._screenManager.populate(this.screen, this, this._isDiagnosticQuiz);
  }

  _showAndTimePhase(screen, phase) {
    phase = phase || screen;
    this.result.beginPhase(phase);
    this._screenManager.show(screen, this._isDiagnosticQuiz);
  }

  _initSentence() {
    const sentences = _.shuffle(this.itemPackage.sentences);
    // Find the first sentence meeting the quiz criteria.
    this.sentence = _.find(sentences, (sentence) => {
      if (this.requiresSentenceAudio && !sentence.sound) {
        return false;
      } else if (this.requiresSentenceTranslation && (!sentence.response || sentence.response.text.length === 0)) {
        return false;
      } else {
        return true;
      }
    });

    if (this.requiresSentence && !this.sentence) {
      // No eligible sentence found.
      Logger.error('Quiz', 'No eligible sentence found for item where a sentence is required.', this);
    }
  }
}

export default ItemQuizBase;
